/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: split-web
 *
 * Activar plan Crece — web (Mercado Pago).
 * Diseño: proto `billing-activar-crece-vs-hoy-v1` (eyebrow → título → precio →
 * bullets con dot brand → CTA crece + link secundario).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts';
import { typography } from '../../shared/theme/typography';
import { PayerEmailRow } from './components/PayerEmailRow';
import {
  creceActivateCopy,
  creceLegacyActivateCopy,
} from '../../../../../shared/plan-crece-copy';
import {
  formatArs,
  formatGrantExpiryLabel,
  planDisplayName,
  planMarketingBullets,
  planPriceArs,
} from '../../../../../shared/billing-catalog';
import { normalizePaidPlanCode, type PaidPlanCode } from '../feature-flags/planIntentService';
import { useBillingWebMercadoPago } from './hooks/useBillingWebMercadoPago';
import { isBillingEnabled } from './isBillingEnabled';
import { trackCreceFunnel } from './trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../shared/crece-funnel';
import { settingsStyles } from '../../settings/screens/settingsScreen.styles';

export function ActivateCreceScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const vm = useBillingWebMercadoPago();
  const { prefetchMpCheckout } = vm;
  const routePlan = normalizePaidPlanCode(route.params?.planCode);
  const [selectedPlan, setSelectedPlan] = useState<PaidPlanCode>(routePlan ?? 'emprende');

  useEffect(() => {
    ensureTicketFonts();
    trackCreceFunnel(CRECE_FUNNEL_EVENTS.activarView, { platform: 'web' });
  }, []);

  useEffect(() => {
    if (routePlan) {
      setSelectedPlan(routePlan);
    } else if (vm.subscribedTier === 'emprende') {
      setSelectedPlan('crece');
    }
  }, [routePlan, vm.subscribedTier]);

  const organizationPlan = (vm.entitlements?.plan_code ?? '').trim().toLowerCase();
  const isLegacy = organizationPlan === 'legacy';
  const planCode: PaidPlanCode = isLegacy ? 'crece' : selectedPlan;
  const planName = planDisplayName(planCode);
  const priceLabel = formatArs(planPriceArs(vm.catalog, planCode));
  const bullets = useMemo(() => planMarketingBullets(vm.catalog, planCode), [vm.catalog, planCode]);
  const isOwner = vm.entitlements?.is_owner === true;
  const isEmployee = vm.entitlements != null && vm.entitlements.is_owner === false;
  const isPaidCrece = vm.subscribedTier === 'crece';
  const isCurrentPlan = vm.subscribedTier === planCode;
  const isEmprendeUpgrade = vm.subscribedTier === 'emprende' && planCode === 'crece';
  const busy = vm.subscribing === planCode;
  const planOptions: PaidPlanCode[] = isLegacy ? ['crece'] : ['emprende', 'crece'];

  const goHome = useCallback(() => navigation.navigate('MainTabs' as never), [navigation]);
  const goBilling = useCallback(() => navigation.navigate('Billing' as never), [navigation]);

  // Prefetch del init_point: el preapproval pending se crea al montar la
  // pantalla, así el click en "Pagar" redirige casi sin espera.
  useEffect(() => {
    if (isOwner && !isCurrentPlan) {
      prefetchMpCheckout(planCode);
    }
  }, [isOwner, isCurrentPlan, planCode, prefetchMpCheckout]);

  if (!isBillingEnabled()) {
    return (
      <ModalScrollScreen variant="form" contentContainerStyle={styles.gap}>
        <View style={settingsStyles.card}>
          <Text style={settingsStyles.cardTitle}>Sin abono</Text>
          <Text style={settingsStyles.caption}>Vuelto no cobra abono ni comisión por venta.</Text>
        </View>
      </ModalScrollScreen>
    );
  }

  if (vm.loading && !vm.entitlements) {
    return (
      <ModalScrollScreen variant="form" contentContainerStyle={styles.gap}>
        <ActivityIndicator color={ticketColors.gold} />
      </ModalScrollScreen>
    );
  }

  if (isEmployee) {
    return (
      <ModalScrollScreen variant="form" contentContainerStyle={styles.gap}>
        <View style={styles.card}>
          <Text style={styles.headline}>El plan lo activa el dueño</Text>
          <Text style={styles.sub}>Pedile a quien administra el negocio que lo elija desde Plan y facturación.</Text>
          <TouchableOpacity style={styles.linkBtn} onPress={goHome}>
            <Text style={styles.linkBtnText}>Ir a Inicio</Text>
          </TouchableOpacity>
        </View>
      </ModalScrollScreen>
    );
  }

  if (isPaidCrece) {
    return (
      <ModalScrollScreen variant="form" contentContainerStyle={styles.gap}>
        <View style={styles.card}>
          <Text style={styles.headline}>El plan Crece ya está activo</Text>
          <Text style={styles.sub}>Para ver o cancelar tu suscripción, entrá a Plan y facturación.</Text>
          <TouchableOpacity style={styles.primaryBtn} onPress={goBilling}>
            <Text style={styles.primaryBtnText}>Ver Plan y facturación</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkBtn} onPress={goHome}>
            <Text style={styles.linkBtnText}>Ir a Inicio</Text>
          </TouchableOpacity>
        </View>
      </ModalScrollScreen>
    );
  }

  if (isCurrentPlan) {
    return (
      <ModalScrollScreen variant="form" contentContainerStyle={styles.gap}>
        <View style={styles.card}>
          <Text style={styles.eyebrow}>Vuelto {planName}</Text>
          <Text style={styles.headline}>El plan {planName} ya está activo</Text>
          <Text style={styles.sub}>Para ver tu suscripción, entrá a Plan y facturación.</Text>
          <TouchableOpacity style={styles.primaryBtn} onPress={goBilling}>
            <Text style={styles.primaryBtnText}>Ver Plan y facturación</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkBtn} onPress={goHome}>
            <Text style={styles.linkBtnText}>Ir a Inicio</Text>
          </TouchableOpacity>
        </View>
      </ModalScrollScreen>
    );
  }

  const untilNextCharge = vm.entitlements?.subscription_expires_at
    ? formatGrantExpiryLabel(vm.entitlements.subscription_expires_at)
    : null;

  return (
    <ModalScrollScreen variant="form" contentContainerStyle={styles.gap}>
      {isLegacy ? (
        <View style={styles.legacyBadge}>
          <Text style={styles.legacyBadgeTitle}>{creceLegacyActivateCopy.badgeTitle}</Text>
          <Text style={styles.legacyBadgeBody}>{creceLegacyActivateCopy.badgeBody}</Text>
        </View>
      ) : null}

      <View style={styles.card}>
        {!isLegacy ? (
          <View style={styles.intro}>
            <Text style={styles.eyebrow}>Elegí un plan</Text>
            <Text style={styles.headline}>Un local, dos formas de avanzar</Text>
          </View>
        ) : (
          <Text style={styles.leadPitch}>{creceLegacyActivateCopy.supportPitch}</Text>
        )}

        {!isLegacy ? (
          <View style={styles.planChoices} accessibilityRole="radiogroup">
            {planOptions.map((option) => {
              const optionName = planDisplayName(option);
              const selected = selectedPlan === option;
              return (
                <TouchableOpacity
                  key={option}
                  style={[styles.planChoice, selected && styles.planChoiceSelected]}
                  onPress={() => setSelectedPlan(option)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected }}
                >
                  <Text style={styles.planChoiceName}>Plan {optionName}</Text>
                  <Text style={styles.planChoicePrice}>{formatArs(planPriceArs(vm.catalog, option))} / mes</Text>
                  <Text style={styles.planChoiceDescription}>
                    {option === 'emprende' ? 'Un local y una persona, con más capacidad mensual.' : 'Equipo, sucursales y uso sin tope.'}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        ) : null}

        <View style={styles.intro}>
          <Text style={styles.eyebrow}>Vuelto {planName}</Text>
          <Text style={styles.headline}>
            {isLegacy ? creceLegacyActivateCopy.ctaPrimary : planCode === 'emprende' ? 'Más margen para tu local' : creceActivateCopy.headline}
          </Text>
        </View>

        <View style={styles.priceRow}>
          <Text style={styles.priceValue}>{priceLabel}</Text>
          <Text style={styles.priceSuffix}>/ mes</Text>
        </View>
        {!isLegacy ? <Text style={styles.tiny}>Cancelás cuando quieras.</Text> : null}

        <View style={styles.bullets}>
          {bullets.map((bullet) => (
            <View key={bullet} style={styles.bulletRow}>
              <View style={styles.bulletDot} accessibilityElementsHidden />
              <Text style={styles.bullet}>{bullet}</Text>
            </View>
          ))}
        </View>

        {isEmprendeUpgrade ? (
          <View style={styles.upgradeNotice}>
            <Text style={styles.upgradeNoticeText}>
              El plan Crece queda activo al confirmar. El próximo cobro se hace cuando termine tu período de Emprende{untilNextCharge ? `, el ${untilNextCharge}` : ''}.
            </Text>
          </View>
        ) : null}

        {isOwner ? (
          <View style={styles.checkoutBlock}>
            <TouchableOpacity
              accessibilityRole="button"
              disabled={busy}
              onPress={() => {
                trackCreceFunnel(CRECE_FUNNEL_EVENTS.payRedirect, {
                  channel: 'mercadopago',
                  plan_code: planCode,
                });
                void vm.startMpCheckout(planCode);
              }}
              style={[styles.primaryBtn, busy && styles.primaryBtnBusy]}
            >
              {busy ? (
                <ActivityIndicator color={ticketColors.creceInk} />
              ) : (
                <Text style={styles.primaryBtnText}>{creceActivateCopy.ctaPayMp}</Text>
              )}
            </TouchableOpacity>
            <Text style={styles.supportLine}>
              {creceActivateCopy.mpTrustLine}
            </Text>
            <PayerEmailRow email={vm.payerEmail} defaultEmail={vm.defaultPayerEmail} onChange={vm.setPayerEmail} />
            {vm.subError ? <Text style={styles.error}>{vm.subError}</Text> : null}
          </View>
        ) : null}

        <TouchableOpacity style={styles.linkBtn} onPress={goHome} disabled={busy}>
          <Text style={styles.linkBtnText}>{isLegacy ? creceLegacyActivateCopy.ctaSecondary : 'Volver a Inicio'}</Text>
        </TouchableOpacity>

        {!isLegacy && planCode === 'crece' ? (
          <Text style={styles.supportLine}>{creceActivateCopy.supportLine}</Text>
        ) : null}
        <Text style={styles.supportLine}>{creceActivateCopy.perOrgLine}</Text>
        {vm.mpBanner ? <Text style={styles.supportLine}>{vm.mpBanner}</Text> : null}
      </View>
    </ModalScrollScreen>
  );
}

const styles = StyleSheet.create({
  gap: { gap: spacing.lg, paddingBottom: spacing.xl },
  /** Contenedor de decisión: mismo patrón que CreceSuccessScreen / settings card. */
  card: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.lg,
    gap: spacing.lg,
  },
  intro: { gap: spacing.xs },
  eyebrow: {
    fontFamily: ticketFonts.sans,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
  },
  headline: {
    ...typography.titleLg,
    fontFamily: ticketFonts.sans,
    fontSize: 22,
    lineHeight: 26,
    letterSpacing: -0.6,
    color: ticketColors.ink,
    fontWeight: '800',
  },
  /** Pitch legacy: mensaje largo → párrafo lead, no display. */
  leadPitch: {
    ...typography.body,
    fontFamily: ticketFonts.sans,
    fontSize: 17,
    lineHeight: 25,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  sub: {
    ...typography.body,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    lineHeight: 22,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.xs,
  },
  priceValue: {
    fontFamily: ticketFonts.sans,
    fontSize: 28,
    fontWeight: '800',
    letterSpacing: -0.8,
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
  },
  priceSuffix: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  tiny: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    lineHeight: 18,
    marginTop: -spacing.sm,
  },
  planChoices: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  planChoice: {
    flexGrow: 1,
    flexBasis: 180,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
    gap: spacing.xs,
  },
  planChoiceSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  planChoiceName: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    fontWeight: '800',
    fontSize: 14,
  },
  planChoicePrice: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    fontWeight: '800',
    fontSize: 18,
    fontVariant: ['tabular-nums'],
  },
  planChoiceDescription: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    fontSize: 12,
    lineHeight: 17,
  },
  upgradeNotice: {
    borderWidth: 1,
    borderColor: ticketColors.brandBorder,
    borderRadius: 10,
    backgroundColor: ticketColors.brandSoft,
    padding: spacing.md,
  },
  upgradeNoticeText: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  bullets: { gap: spacing.sm },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  bulletDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ticketColors.gold,
    marginTop: 7,
  },
  bullet: {
    ...typography.body,
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.ink,
    lineHeight: 20,
    flex: 1,
  },
  checkoutBlock: { gap: spacing.sm },
  primaryBtn: {
    borderRadius: 10,
    backgroundColor: ticketColors.crece,
    paddingVertical: spacing.md,
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnBusy: { opacity: 0.7 },
  primaryBtnText: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.creceInk,
    fontWeight: '800',
    fontSize: 16,
  },
  /** Secundario estilo link (proto `.btn-link`): sin caja, brand, centrado. */
  linkBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    minHeight: 40,
  },
  linkBtnText: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.gold,
    fontWeight: '700',
    fontSize: 14,
  },
  supportLine: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink3,
    lineHeight: 18,
    textAlign: 'center',
  },
  /** Banner agradecimiento legacy (proto `.banner.teal`): soft teal, no warning. */
  legacyBadge: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.brandBorder,
    backgroundColor: ticketColors.brandSoft,
    paddingHorizontal: 14,
    paddingVertical: spacing.md,
    gap: spacing.xs,
  },
  legacyBadgeTitle: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  legacyBadgeBody: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 19,
  },
  error: {
    fontFamily: ticketFonts.sans,
    color: ticketColors.bad,
  },
});
